import { Link, useParams } from "react-router";
import { z } from "zod";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRoster } from "@/lib/hooks";
import { formatCurrency, formatDollars } from "@/lib/formatters";
import { STATUS_LABELS } from "@/lib/roster";
import type { CreateOrderInput, Employee } from "@/lib/api";
import { usePaymentSubmission } from "@/lib/payment-flow";
import {
  addCardIssues,
  buildPaymentFields,
  paymentFormShape,
  scrollToFirstError,
  useRosterCheckoutSuccess,
} from "@/lib/checkout-form";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorMessage } from "@/components/ErrorMessage";
import {
  CheckoutPaymentSection,
  CheckoutSubmitSection,
} from "@/components/RosterCheckoutParts";

const completionSchema = z
  .object({ ...paymentFormShape })
  .superRefine((data, ctx) => addCardIssues(data.card, ctx));

type CompletionFormData = z.infer<typeof completionSchema>;

function CompletionForm({
  employee,
  amountOwed,
}: {
  employee: Employee;
  amountOwed: number;
}): React.ReactElement {
  const onSuccess = useRosterCheckoutSuccess();

  const methods = useForm<CompletionFormData>({
    resolver: zodResolver(completionSchema),
    defaultValues: {
      billingAddress: { street: "", city: "", state: "", zip: "" },
      paymentMethod: "credit_card",
      card: { cardNumber: "", expireMonth: "", expireYear: "", cvv: "", nameOnCard: "" },
    },
  });

  // Literal flow: this page's own key slot, never the public checkout's. The body
  // names the employee and carries no amount: the server charges the figure it
  // stored on the employee record.
  const payment = usePaymentSubmission<CompletionFormData>({
    flow: "completion",
    buildOrder: (data, idempotencyKey): CreateOrderInput => ({
      type: "completion",
      employeeIds: [employee.id],
      ...buildPaymentFields(data, idempotencyKey),
    }),
    onSuccess,
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => void methods.handleSubmit(payment.submit, scrollToFirstError)(e)}
        noValidate
        className="space-y-8"
      >
        <section className="rounded-md border border-gray-200 bg-white p-4">
          <p className="text-sm font-medium text-gray-900">
            {employee.firstName} {employee.lastName}
          </p>
          <p className="mt-1 text-sm text-gray-700">
            Complete full background check — {formatDollars(amountOwed)}
          </p>
          <p className="mt-2 text-xs text-gray-500">
            They were not found in the Florida Clearinghouse. The amount shown is what remains
            after the badge you already paid for.
          </p>
        </section>

        <CheckoutPaymentSection />

        <CheckoutSubmitSection
          payment={payment}
          label={`Place Order — ${formatCurrency(amountOwed)}`}
        />
      </form>
    </FormProvider>
  );
}

function BackToRoster(): React.ReactElement {
  return (
    <Link to="/roster" className="mt-2 inline-block font-medium text-blue-700 hover:text-blue-900">
      Back to My Employees
    </Link>
  );
}

function Notice({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="rounded-md border border-gray-200 bg-white p-4 text-sm text-gray-700">
      <p>{children}</p>
      <BackToRoster />
    </div>
  );
}

export function CompletionCheckoutPage(): React.ReactElement {
  const { employeeId } = useParams();
  const roster = useRoster();

  let body: React.ReactElement;
  if (roster.isLoading) {
    body = <LoadingSpinner />;
  } else if (roster.error) {
    body = (
      <ErrorMessage
        message={roster.error instanceof Error ? roster.error.message : "Failed to load your employees"}
      />
    );
  } else {
    const employee = roster.data?.employees.find((e) => e.id === employeeId);
    if (!employee) {
      body = <Notice>We could not find that employee in My Employees.</Notice>;
    } else if (employee.inactive) {
      body = (
        <Notice>
          {employee.firstName} {employee.lastName} is marked as no longer employed, so nothing can be
          ordered for them. Reactivate them from My Employees first.
        </Notice>
      );
    } else if (employee.status !== "attestation_not_found") {
      // The roster is the source of truth: a completion already paid for, or an
      // employee who never needed one, is not payable here.
      body = (
        <Notice>
          {employee.firstName} {employee.lastName} does not need a background check completed right now
          ({STATUS_LABELS[employee.status]}).
        </Notice>
      );
    } else if (employee.completionPriceOwed === undefined || employee.completionPriceOwed <= 0) {
      // Display only (the server charges its stored value) — but with no amount
      // on record there is nothing honest to show or to ask the vendor to confirm.
      body = (
        <Notice>
          The amount owed for {employee.firstName} {employee.lastName} is not recorded yet, so the
          background check cannot be ordered here. Please contact the Safe Environment office.
        </Notice>
      );
    } else {
      // Keyed by employee: a route change without an unmount must never carry
      // card fields or a held payment from one employee to the next.
      body = (
        <CompletionForm
          key={employee.id}
          employee={employee}
          amountOwed={employee.completionPriceOwed}
        />
      );
    }
  }

  return (
    <div className="mx-auto max-w-[640px]">
      <h1 className="mb-6 text-2xl font-bold text-gray-900">Complete background check</h1>
      {body}
    </div>
  );
}
